import React from 'react';
import {
  IAddress,
  ICentreInfo,
  IGeopoint,
  IImage,
  TThemeColor
} from '@mayfield/sanity';
import {
  Button,
  IntersectionAnimation,
  LayoutProvider,
  PortableTextRenderer,
  SVG
} from '@mayfield/common/components';
import cn from 'classnames';
import { mapStateToInitials } from '@mayfield/common/utils';
import {
  useBreakpoints,
  useIntersectionAnimation
} from '@mayfield/common/hooks';
import { useApp, useGoogleMapsScript } from '@mayfield/website/hooks';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getGatsbyImageData } from 'gatsby-source-sanity';
import { Map } from '@mayfield/website/components';
import { LoadScript } from '@react-google-maps/api';
import * as styles from './styles.module.scss';

interface IProps extends ICentreInfo {
  centreTitle: string;
  themeColor: TThemeColor;
  address: IAddress;
  id?: string;
  logo: IImage;
  location: IGeopoint;
}

const CentreInfo = ({
  contactNumber,
  _rawDescription,
  address,
  header,
  openingHours,
  facebook,
  instagram,
  centreTitle,
  themeColor,
  location,
  logo,
  id
}: IProps) => {
  const { setBookATourSelectedCentre, setIsBookATourSidebarOpen } = useApp();
  const { largeTablet } = useBreakpoints();

  const formattedAddress = `${address.streetAddress}\n${
    address.suburb
  }, ${mapStateToInitials(address.state)} ${address.postcode}`;

  const { ref, inView } = useIntersectionAnimation();

  const { API_KEY, scriptHasLoaded, setScriptHasLoaded } =
    useGoogleMapsScript();

  const handleBookATourClick = () => {
    setIsBookATourSidebarOpen(true);
    setBookATourSelectedCentre({
      label: centreTitle,
      value: centreTitle
    });
  };

  const scrollToId = (id: string) => {
    const domNode = document.getElementById(id);
    if (!domNode) return;

    const headerHeight = largeTablet ? 56 : 52;
    const offsetHeight = headerHeight * 2;

    const distanceFromTopOfDocument =
      window.scrollY + domNode.getBoundingClientRect().top;

    window.scrollTo({
      top: distanceFromTopOfDocument - offsetHeight,
      behavior: 'smooth'
    });
  };

  const handleContactUsClick = () => {
    scrollToId('centreContactForm');
  };

  const headerText = header || centreTitle;

  return (
    <div className={styles.container} id={id}>
      <LayoutProvider grid padding maxWidth>
        <div className={styles.logoContainer}>
          <GatsbyImage
            style={{ width: '100%' }}
            image={logo.asset.gatsbyImageData}
            alt="Mayfield logo"
          />
        </div>

        <div className={styles.mainText} ref={ref}>
          <IntersectionAnimation delay={200} trigger={inView}>
            <h2 className={cn('h1', styles.title)}>{headerText}</h2>
          </IntersectionAnimation>

          <IntersectionAnimation delay={300} trigger={inView}>
            <PortableTextRenderer
              gatsbyImageComponent={GatsbyImage}
              getGatsbyImageData={getGatsbyImageData}
              rawText={_rawDescription}
              textClassName="b1"
            />
          </IntersectionAnimation>
        </div>

        <IntersectionAnimation
          animation="fade"
          delay={600}
          className={cn('b2', styles.info)}
        >
          <div className={styles.infoContent}>
            <div className={styles.address}>{formattedAddress}</div>
            <div className={styles.openingHours}>{openingHours}</div>
            <div className={styles.contactNumber}>{contactNumber}</div>

            <div className={styles.buttons}>
              <Button
                color="sea"
                className={styles.button}
                onClick={handleBookATourClick}
              >
                Book a Tour
              </Button>

              <Button
                color={themeColor}
                className={styles.button}
                onClick={handleContactUsClick}
              >
                Contact Us
              </Button>
            </div>

            {(facebook || instagram) && (
              <div className={styles.socialLinks}>
                {instagram && (
                  <a className={styles.socialLink} href={instagram}>
                    <SVG className={styles.socialIcon} svg="instagram" />
                  </a>
                )}

                {facebook && (
                  <a className={styles.socialLink} href={facebook}>
                    <SVG className={styles.socialIcon} svg="facebook" />
                  </a>
                )}
              </div>
            )}
          </div>

          <div className={styles.mapContainer}>
            <LoadScript
              googleMapsApiKey={API_KEY}
              libraries={['places', 'geometry']}
              onLoad={() => setScriptHasLoaded(true)}
            >
              {scriptHasLoaded && (
                <Map
                  disablePinPopup
                  locations={[
                    {
                      address: '',
                      location,
                      logo,
                      name: '',
                      url: ''
                    }
                  ]} // Blank values not required, saves needing to restructure the data
                  scriptHasLoaded={scriptHasLoaded}
                />
              )}
            </LoadScript>
          </div>
        </IntersectionAnimation>
      </LayoutProvider>
    </div>
  );
};

export default CentreInfo;
